import { Button, Form, Input, notification, Tree, TreeDataNode } from 'core-ui';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Footer from 'src/components/Footer';
import StyledForm from 'src/components/styled-form';
import { PermissionList } from 'src/enums/permission';
import { RoutePath } from 'src/enums/routePath';
import { apiRequest } from 'src/utils/api';
import { trimObjectValues } from 'src/utils/common';
import { ProfileContext } from 'src/contexts/profile';
import { PERMISSIONS } from '../../utils';
import { getPermissionOfAgencyAccount } from 'src/services/admin';
import { getPositionById } from 'src/services/admin/config';
import { FEATURE_FLAGS } from 'src/constants/featureFlags';
import useResponsive from 'src/hooks/useResponsive';

export default function ConfigController() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { fetchProfile, agency } = useContext(ProfileContext);
  const [agencyPermissions, setAgencyPermissions] = useState<any>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { isMobile } = useResponsive();

  const fetchAgencyPermissions = async () => {
    if (agency?.agencyId) {
      try {
        const data = await getPermissionOfAgencyAccount(
          agency.agencyId.toString()
        );
        setAgencyPermissions(data);
      } catch (err) {
        const error = err as any;
        console.error('Error fetching agency permissions:', error);
      }
    }
  };

  // Function to filter tree data based on agency permissions
  const getFilteredTreeData = (
    treeData: TreeDataNode[],
    agencyPermissions: any
  ): TreeDataNode[] => {
    // Use the same structure as Account form
    const allowedPermissions = agencyPermissions?.data?.agencyPermissions || [];

    const filterNode = (node: TreeDataNode): TreeDataNode | null => {
      if (node.children && node.children.length > 0) {
        // For parent nodes, recursively filter children
        const filteredChildren = node.children
          .map(filterNode)
          .filter((child): child is TreeDataNode => child !== null);

        // Only return parent node if it has valid children or if it's the root '[parent]' node
        if (filteredChildren.length > 0 || node.key === '[parent]') {
          return {
            ...node,
            children: filteredChildren
          };
        }
        return null;
      } else {
        // For leaf nodes, check if permission exists in allowedPermissions - same as Account form
        const isAllowed = allowedPermissions.includes(node.key as string);
        return isAllowed ? node : null;
      }
    };

    const filtered = treeData
      .map(filterNode)
      .filter((node): node is TreeDataNode => node !== null);

    return filtered;
  };

  // Get filtered tree data
  const filteredTreeData =
    agency?.agencyId && agencyPermissions?.data?.agencyPermissions
      ? getFilteredTreeData(permissionTreeData, agencyPermissions)
      : permissionTreeData;

  const onSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    let formData = form.getFieldsValue();
    formData.permissions = formData.permissions.filter(
      (key: string) => !key.includes('[parent]')
    );
    formData = trimObjectValues(formData);
    try {
      const { data } = await apiRequest.put({
        url: !id ? '/api/admin/positions' : `/api/admin/positions/${id}`,
        data: formData,
        method: id ? 'put' : 'post'
      });
      fetchProfile && fetchProfile();
      notification.success({ message: data.message });
      navigate(RoutePath.ADMIN + RoutePath.CONFIG);
    } catch (error: any) {
      notification.error({
        message: error?.response?.data && (
          <div
            className={
              isMobile ? 'whitespace-normal break-words' : 'whitespace-nowrap'
            }
          >
            {error.response.data?.message ||
              error.response.data?.error?.[0]?.message}
          </div>
        ),
        style: {
          width: isMobile ? 'calc(100vw - 32px)' : 'auto',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 'none'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchData = async () => {
    if (!id) return;
    try {
      const { data } = await getPositionById(id);
      form.setFieldsValue(data.data);
      form.setFieldValue(
        'permissions',
        data.data.permissions.map((i: any) => i.permission)
      );
    } catch (error: any) {
      notification.error({
        message: error?.response?.data?.message && (
          <div
            className={
              isMobile ? 'whitespace-normal break-words' : 'whitespace-nowrap'
            }
          >
            {error.response.data.message}
          </div>
        ),
        style: {
          width: isMobile ? 'calc(100vw - 32px)' : 'auto',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 'none'
        }
      });
    }
  };

  useEffect(() => {
    fetchData();
    fetchAgencyPermissions();
  }, []);

  return (
    <StyledForm
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      scrollToFirstError={{
        behavior: 'smooth',
        block: 'center'
      }}
      style={{ background: 'rgb(242, 242, 242)' }}
    >
      <div className="bg-white h-[68px] pt-3 pb-4 px-6 ">
        <p className="font-semibold text-2xl">
          {id ? '役割編集' : '役割新規作成'}
        </p>
      </div>
      <div className="bg-white my-5 p-4 mx-4">
        <Input
          allowClear
          rules={[{ required: true, max: 255, whitespace: true }]}
          size="large"
          name="name"
          label="役割"
          style={{ maxWidth: 368 }}
          placeholder="役割"
        />
        <Form.Item
          rules={[{ required: true }]}
          initialValue={[]}
          label="権限設定"
          valuePropName="checkedKeys"
          name="permissions"
          validateTrigger="onCheck"
          trigger="onCheck"
          tooltip={{
            title: (
              <span style={{ maxWidth: 400 }}>
                一覧画面は基本的なアクセス権限です。
                <br />
                他の機能を選択すると、自動的に一覧画面の閲覧権限が与えられます。
              </span>
            ),
            overlayStyle: { maxWidth: 500 },
            placement: 'topRight'
          }}
        >
          <Tree
            selectable={false}
            defaultExpandAll
            checkable
            treeData={filteredTreeData}
          />
        </Form.Item>
      </div>
      <Footer>
        <Button
          style={{ height: 45, width: 168 }}
          onClick={() => navigate(RoutePath.ADMIN + RoutePath.CONFIG)}
        >
          {id ? 'キャンセル' : '戻る'}
        </Button>
        <Button
          style={{ height: 45, width: 168 }}
          type="primary"
          onClick={form.submit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {id ? '保存' : '作成'}
        </Button>
      </Footer>
    </StyledForm>
  );
}

export const permissionTreeData: TreeDataNode[] = [
  {
    title: '全部選択',
    key: '[parent]',
    children: [
      ...(FEATURE_FLAGS.ENABLE_DASHBOARD
        ? [
            {
              title: 'ダッシュボード',
              key: '[parent]dashboard',
              children: [
                {
                  title: '速報値',
                  key: '[parent]dashboardQuickReport',
                  children: [
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_KPI_SUMMARY_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_KPI_SUMMARY_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_REVENUE_STRUCTURE_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_REVENUE_STRUCTURE_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .QUICK_REPORT_MEMBER_SALES_STRUCTURE_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_MEMBER_SALES_STRUCTURE_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_AGENCY_BREAKDOWN_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_AGENCY_BREAKDOWN_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_INQUIRY_WORK_STATUS_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_INQUIRY_WORK_STATUS_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_AGENCY_RANKING_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_AGENCY_RANKING_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_PLAN_RANKING_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_PLAN_RANKING_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_SALES_UP_AGENCIES_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_SALES_UP_AGENCIES_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.QUICK_REPORT_SALES_DOWN_AGENCIES_VIEW
                        ],
                      key: PermissionList.QUICK_REPORT_SALES_DOWN_AGENCIES_VIEW
                    }
                  ]
                },
                {
                  title: '確定値',
                  key: '[parent]dashboardConfirmedReport',
                  children: [
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.CONFIRMED_REPORT_KPI_SUMMARY_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_KPI_SUMMARY_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.CONFIRMED_REPORT_REVENUE_STRUCTURE_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_REVENUE_STRUCTURE_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .CONFIRMED_REPORT_MEMBER_SALES_STRUCTURE_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_MEMBER_SALES_STRUCTURE_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.CONFIRMED_REPORT_AGENCY_BREAKDOWN_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_AGENCY_BREAKDOWN_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .CONFIRMED_REPORT_INQUIRY_WORK_STATUS_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_INQUIRY_WORK_STATUS_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.CONFIRMED_REPORT_AGENCY_RANKING_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_AGENCY_RANKING_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.CONFIRMED_REPORT_PLAN_RANKING_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_PLAN_RANKING_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList.CONFIRMED_REPORT_SALES_UP_AGENCIES_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_SALES_UP_AGENCIES_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .CONFIRMED_REPORT_SALES_DOWN_AGENCIES_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_SALES_DOWN_AGENCIES_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .CONFIRMED_REPORT_BILLING_REVENUE_CHART_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_BILLING_REVENUE_CHART_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .CONFIRMED_REPORT_SUBSCRIPTION_MEMBERS_CHART_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_SUBSCRIPTION_MEMBERS_CHART_VIEW
                    },
                    {
                      title:
                        PERMISSIONS[
                          PermissionList
                            .CONFIRMED_REPORT_SHOT_TRANSACTIONS_CHART_VIEW
                        ],
                      key: PermissionList.CONFIRMED_REPORT_SHOT_TRANSACTIONS_CHART_VIEW
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      {
        title: '決済管理',
        key: '[parent]payment',
        children: [
          {
            title: PERMISSIONS[PermissionList.PAYMENT_MANAGER_LIST],
            key: PermissionList.PAYMENT_MANAGER_LIST
          },
          {
            title: PERMISSIONS[PermissionList.PAYMENT_MANAGER_EXPORT_CSV],
            key: PermissionList.PAYMENT_MANAGER_EXPORT_CSV
          }
        ]
      },
      {
        title: '売上管理',
        key: '[parent]sales',
        children: [
          {
            title: PERMISSIONS[PermissionList.SALES_MANAGER_LIST],
            key: PermissionList.SALES_MANAGER_LIST
          },
          {
            title: PERMISSIONS[PermissionList.SALES_MANAGER_EXPORT_CSV],
            key: PermissionList.SALES_MANAGER_EXPORT_CSV
          }
        ]
      },
      {
        title: '代理店',
        key: '[parent]agency',
        children: [
          {
            title: PERMISSIONS[PermissionList.AGENCY_LIST],
            key: PermissionList.AGENCY_LIST
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_CREATE],
            key: PermissionList.AGENCY_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_DETAIL],
            key: PermissionList.AGENCY_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_EDIT],
            key: PermissionList.AGENCY_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_DELETE],
            key: PermissionList.AGENCY_DELETE
          },
          {
            title: '販売プラン（傘下代理店対象）',
            key: '[parent]inheritedPlans',
            children: [
              {
                title: PERMISSIONS[PermissionList.AGENCY_INHERITED_PLAN_ADD],
                key: PermissionList.AGENCY_INHERITED_PLAN_ADD
              },
              {
                title: PERMISSIONS[PermissionList.AGENCY_INHERITED_PLAN_DELETE],
                key: PermissionList.AGENCY_INHERITED_PLAN_DELETE
              }
            ]
          },
          {
            title: '販売プラン（傘下代理店非対象）',
            key: '[parent]excludedPlans',
            children: [
              {
                title: PERMISSIONS[PermissionList.AGENCY_EXCLUDED_PLAN_ADD],
                key: PermissionList.AGENCY_EXCLUDED_PLAN_ADD
              },
              {
                title: PERMISSIONS[PermissionList.AGENCY_EXCLUDED_PLAN_DELETE],
                key: PermissionList.AGENCY_EXCLUDED_PLAN_DELETE
              }
            ]
          }
        ]
      },
      {
        title: '会員情報',
        key: '[parent]client',
        children: [
          {
            title: PERMISSIONS[PermissionList.CLIENT_LIST],
            key: PermissionList.CLIENT_LIST
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_IMPORT_CSV],
            key: PermissionList.CLIENT_IMPORT_CSV
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_EXPORT_CSV],
            key: PermissionList.CLIENT_EXPORT_CSV
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_SEARCH_LIST],
            key: PermissionList.CLIENT_SEARCH_LIST
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_CREATE],
            key: PermissionList.CLIENT_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_DETAIL],
            key: PermissionList.CLIENT_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_EDIT],
            key: PermissionList.CLIENT_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.CLIENT_DELETE],
            key: PermissionList.CLIENT_DELETE
          }
        ]
      },
      {
        title: '契約情報',
        key: '[parent]customer',
        children: [
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_LIST],
            key: PermissionList.CUSTOMER_LIST
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_IMPORT_CSV],
            key: PermissionList.CUSTOMER_IMPORT_CSV
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_EXPORT_CSV],
            key: PermissionList.CUSTOMER_EXPORT_CSV
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_SEARCH_LIST],
            key: PermissionList.CUSTOMER_SEARCH_LIST
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_CREATE],
            key: PermissionList.CUSTOMER_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_DETAIL],
            key: PermissionList.CUSTOMER_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_EDIT],
            key: PermissionList.CUSTOMER_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.CUSTOMER_DELETE],
            key: PermissionList.CUSTOMER_DELETE
          }
        ]
      },
      {
        title: '修理管理',
        key: '[parent]repair',
        children: [
          {
            title: PERMISSIONS[PermissionList.REPAIR_LIST],
            key: PermissionList.REPAIR_LIST
          },
          {
            title: PERMISSIONS[PermissionList.REPAIR_CREATE],
            key: PermissionList.REPAIR_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.REPAIR_DETAIL],
            key: PermissionList.REPAIR_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.REPAIR_EDIT],
            key: PermissionList.REPAIR_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.REPAIR_DELETE],
            key: PermissionList.REPAIR_DELETE
          }
        ]
      },
      {
        title: '同意書管理',
        key: '[parent]submit_agreement',
        children: [
          {
            title: PERMISSIONS[PermissionList.SUBMIT_AGREEMENT_LIST],
            key: PermissionList.SUBMIT_AGREEMENT_LIST
          },
          {
            title: PERMISSIONS[PermissionList.SUBMIT_AGREEMENT_SEARCH_LIST],
            key: PermissionList.SUBMIT_AGREEMENT_SEARCH_LIST
          },
          {
            title: PERMISSIONS[PermissionList.SUBMIT_AGREEMENT_CREATE],
            key: PermissionList.SUBMIT_AGREEMENT_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.SUBMIT_AGREEMENT_EDIT],
            key: PermissionList.SUBMIT_AGREEMENT_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.SUBMIT_AGREEMENT_DELETE],
            key: PermissionList.SUBMIT_AGREEMENT_DELETE
          }
        ]
      },
      {
        title: 'アカウント管理',
        key: '[parent]account',
        children: [
          {
            title: 'アカウント一覧',
            key: PermissionList.ACCOUNT_LIST
          },
          {
            title: PERMISSIONS[PermissionList.ACCOUNT_CREATE],
            key: PermissionList.ACCOUNT_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.ACCOUNT_DETAIL],
            key: PermissionList.ACCOUNT_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.ACCOUNT_EDIT],
            key: PermissionList.ACCOUNT_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.ACCOUNT_DELETE],
            key: PermissionList.ACCOUNT_DELETE
          }
        ]
      },
      {
        title: '申込用QRコード管理',
        key: '[parent]qrRegister',
        children: [
          {
            title: PERMISSIONS[PermissionList.QR_REGISTER_LIST],
            key: PermissionList.QR_REGISTER_LIST
          },
          {
            title: PERMISSIONS[PermissionList.QR_REGISTER_CREATE],
            key: PermissionList.QR_REGISTER_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.QR_REGISTER_DELETE],
            key: PermissionList.QR_REGISTER_DELETE
          }
        ]
      },
      {
        title: '権限設定',
        key: '[parent]permission',
        children: [
          {
            title: PERMISSIONS[PermissionList.PERMISSION_LIST],
            key: PermissionList.PERMISSION_LIST
          },
          {
            title: PERMISSIONS[PermissionList.PERMISSION_CREATE],
            key: PermissionList.PERMISSION_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.PERMISSION_EDIT],
            key: PermissionList.PERMISSION_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.PERMISSION_DELETE],
            key: PermissionList.PERMISSION_DELETE
          }
        ]
      },
      {
        title: '代理店権限設定',
        key: '[parent]agencyAuthority',
        children: [
          {
            title: PERMISSIONS[PermissionList.AGENCY_AUTHORITY_LIST],
            key: PermissionList.AGENCY_AUTHORITY_LIST
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_AUTHORITY_DETAIL],
            key: PermissionList.AGENCY_AUTHORITY_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_AUTHORITY_EDIT],
            key: PermissionList.AGENCY_AUTHORITY_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.AGENCY_AUTHORITY_APPROVED],
            key: PermissionList.AGENCY_AUTHORITY_APPROVED
          }
        ]
      },
      {
        title: '閲覧期限設定',
        key: PermissionList.ENABLING_REPAIR_AGREEMENT_LINK
      },
      ...(FEATURE_FLAGS.ENABLE_COUNT_SETTING_NUMBER
        ? [
            {
              title: '件数設定',
              key: '[parent]countSettings',
              children: [
                {
                  title: '入電件数・応答件数',
                  key: '[parent]numberOfCalls',
                  children: [
                    {
                      title: PERMISSIONS[PermissionList.COUNT_CALLS_LIST],
                      key: PermissionList.COUNT_CALLS_LIST
                    },
                    {
                      title: PERMISSIONS[PermissionList.COUNT_CALLS_CREATE],
                      key: PermissionList.COUNT_CALLS_CREATE
                    },
                    {
                      title: PERMISSIONS[PermissionList.COUNT_CALLS_EDIT],
                      key: PermissionList.COUNT_CALLS_EDIT
                    },
                    {
                      title: PERMISSIONS[PermissionList.COUNT_CALLS_DELETE],
                      key: PermissionList.COUNT_CALLS_DELETE
                    }
                  ]
                },
                {
                  title: '修理件数',
                  key: '[parent]numberOfRepairs',
                  children: [
                    {
                      title: PERMISSIONS[PermissionList.COUNT_REPAIRS_LIST],
                      key: PermissionList.COUNT_REPAIRS_LIST
                    },
                    {
                      title: PERMISSIONS[PermissionList.COUNT_REPAIRS_CREATE],
                      key: PermissionList.COUNT_REPAIRS_CREATE
                    },
                    {
                      title: PERMISSIONS[PermissionList.COUNT_REPAIRS_EDIT],
                      key: PermissionList.COUNT_REPAIRS_EDIT
                    },
                    {
                      title: PERMISSIONS[PermissionList.COUNT_REPAIRS_DELETE],
                      key: PermissionList.COUNT_REPAIRS_DELETE
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      {
        title: 'メーカー名マスター',
        key: '[parent]maker',
        children: [
          {
            title: PERMISSIONS[PermissionList.MAKER_LIST],
            key: PermissionList.MAKER_LIST
          },
          {
            title: PERMISSIONS[PermissionList.MAKER_CREATE],
            key: PermissionList.MAKER_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.MAKER_EDIT],
            key: PermissionList.MAKER_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.MAKER_DELETE],
            key: PermissionList.MAKER_DELETE
          }
        ]
      },
      {
        title: '型番マスター',
        key: '[parent]model',
        children: [
          {
            title: PERMISSIONS[PermissionList.MODEL_LIST],
            key: PermissionList.MODEL_LIST
          },
          {
            title: PERMISSIONS[PermissionList.MODEL_CREATE],
            key: PermissionList.MODEL_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.MODEL_EDIT],
            key: PermissionList.MODEL_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.MODEL_DELETE],
            key: PermissionList.MODEL_DELETE
          }
        ]
      },
      {
        title: '製品カテゴリーマスター',
        key: '[parent]category',
        children: [
          {
            title: PERMISSIONS[PermissionList.CATEGORY_LIST],
            key: PermissionList.CATEGORY_LIST
          },
          {
            title: PERMISSIONS[PermissionList.CATEGORY_CREATE],
            key: PermissionList.CATEGORY_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.CATEGORY_EDIT],
            key: PermissionList.CATEGORY_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.CATEGORY_DELETE],
            key: PermissionList.CATEGORY_DELETE
          }
        ]
      },
      {
        title: 'プランマスター',
        key: '[parent]plans',
        children: [
          {
            title: PERMISSIONS[PermissionList.PLAN_LIST],
            key: PermissionList.PLAN_LIST
          },
          {
            title: PERMISSIONS[PermissionList.PLAN_CREATE],
            key: PermissionList.PLAN_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.PLAN_DETAIL],
            key: PermissionList.PLAN_DETAIL
          },
          {
            title: PERMISSIONS[PermissionList.PLAN_EDIT],
            key: PermissionList.PLAN_EDIT
          },
          {
            title: PERMISSIONS[PermissionList.PLAN_DELETE],
            key: PermissionList.PLAN_DELETE
          },
          {
            title: 'メール本文設定',
            key: '[parent]mailSettings',
            children: [
              {
                title: '申込完了メール',
                key: '[parent]mailPlanComplete',
                children: [
                  {
                    title:
                      PERMISSIONS[PermissionList.MAIL_PLAN_COMPLETE_DETAIL],
                    key: PermissionList.MAIL_PLAN_COMPLETE_DETAIL
                  },
                  {
                    title: PERMISSIONS[PermissionList.MAIL_PLAN_COMPLETE_EDIT],
                    key: PermissionList.MAIL_PLAN_COMPLETE_EDIT
                  }
                ]
              },
              {
                title: 'マイページ情報メール',
                key: '[parent]mailLogin',
                children: [
                  {
                    title: PERMISSIONS[PermissionList.MAIL_LOGIN_DETAIL],
                    key: PermissionList.MAIL_LOGIN_DETAIL
                  },
                  {
                    title: PERMISSIONS[PermissionList.MAIL_LOGIN_EDIT],
                    key: PermissionList.MAIL_LOGIN_EDIT
                  }
                ]
              },
              {
                title: '決済成功メール',
                key: '[parent]mailPaymentSuccess',
                children: [
                  {
                    title:
                      PERMISSIONS[PermissionList.MAIL_PAYMENT_SUCCESS_DETAIL],
                    key: PermissionList.MAIL_PAYMENT_SUCCESS_DETAIL
                  },
                  {
                    title:
                      PERMISSIONS[PermissionList.MAIL_PAYMENT_SUCCESS_EDIT],
                    key: PermissionList.MAIL_PAYMENT_SUCCESS_EDIT
                  }
                ]
              },
              {
                title: '決済失敗メール',
                key: '[parent]mailPaymentFail',
                children: [
                  {
                    title: PERMISSIONS[PermissionList.MAIL_PAYMENT_FAIL_DETAIL],
                    key: PermissionList.MAIL_PAYMENT_FAIL_DETAIL
                  },
                  {
                    title: PERMISSIONS[PermissionList.MAIL_PAYMENT_FAIL_EDIT],
                    key: PermissionList.MAIL_PAYMENT_FAIL_EDIT
                  }
                ]
              },
              {
                title: PERMISSIONS[PermissionList.TEST_SEND_MAIL],
                key: PermissionList.TEST_SEND_MAIL
              }
            ]
          }
        ]
      },
      {
        title: 'IMEI情報取得関連',
        key: '[parent]imei',
        children: [
          {
            title: PERMISSIONS[PermissionList.IMEI_CREATE],
            key: PermissionList.IMEI_CREATE
          },
          {
            title: PERMISSIONS[PermissionList.IMEI_EDIT],
            key: PermissionList.IMEI_EDIT
          }
        ]
      }
    ]
  }
];
